import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { MonthYear } from '../../../utils/MonthYear/monthYear';

export interface SalesCommItem {
  id: string;
  materialId: string;
  value: number;
  date: Date;
  orderId: string;
  clientName: string;
  actualPrice?: number;
  uuid?: string;
  dontSum?: boolean;
  suppliedQty?: number;
  accountId?: string;
}

export const flattenSalesCommits = (futureOrders: IFutureOrder[]): SalesCommItem[] =>
  futureOrders.reduce(
    (acc, { orderId, monthExposure, clientName, orderDetails, uuid, dontSum, accountId }) => [
      ...acc,
      ...(Object.entries(monthExposure || {}).reduce(
        (innerAcc: any[], [materialId, exposureCalendar]) => [
          ...innerAcc,
          ...exposureCalendar.map(({ date, value }: { date: MonthYear; value: number }) => ({
            orderId,
            materialId,
            clientName,
            actualPrice: orderDetails.pricePerUnit,
            date: toDate(date),
            value,
            uuid,
            dontSum,
            accountId,
            // Create a unique ID based on uuid + materialId + year + month
            id: `${uuid || orderId}-${materialId}-${date.year}-${date.month}`,
          })),
        ],
        [] as SalesCommItem[],
      ) || []),
    ],
    [] as SalesCommItem[],
  );

const toDate = ({ year, month }: MonthYear): Date => new Date(Date.UTC(year, month - 1));

export const takeEarlier = (date1: Date, date2?: Date): Date => (date2 && date1 > date2 ? date2 : date1);
export const takeLater = (date1: Date, date2?: Date): Date => (date2 && date2 > date1 ? date2 : date1);

export const groupSalesByMaterial = (
  salesCommits: SalesCommItem[],
): Record<string, { fromDate: Date; toDate: Date; quantity: number }> =>
  salesCommits
    .filter((item) => !item.dontSum)
    .reduce((acc, { materialId, date, value: quantity, suppliedQty }) => {
      const remainingQty = quantity - (suppliedQty || 0);
      return {
        ...acc,
        [materialId]: {
          fromDate: takeEarlier(date, acc[materialId]?.fromDate),
          toDate: takeLater(date, acc[materialId]?.toDate),
          quantity: remainingQty + (acc[materialId]?.quantity || 0),
        },
      };
    }, {} as Record<string, { fromDate: Date; toDate: Date; quantity: number }>);

export interface GroupedSalesCommItem extends SalesCommItem {
  isGroup?: boolean;
  groupKey?: string;
  children?: SalesCommItem[];
  totalQuantity?: number;
  totalActualPrice?: number;
  totalValue?: number;
}

export const groupSalesCommitsByMonth = (salesCommits: SalesCommItem[]): GroupedSalesCommItem[] => {
  const grouped = salesCommits.reduce((acc, item) => {
    const monthKey = `${item.date.getFullYear()}-${String(item.date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(item);
    return acc;
  }, {} as Record<string, SalesCommItem[]>);

  return Object.entries(grouped)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .flatMap(([monthKey, items]) => {
      const totalQuantity = items.filter((item) => !item.dontSum).reduce((sum, item) => sum + item.value, 0);
      const totalActualPrice = items
        .filter((item) => !item.dontSum)
        .reduce((sum, item) => sum + (item.actualPrice || 0) * item.value, 0);

      const groupHeader: GroupedSalesCommItem = {
        id: `group-${monthKey}` as any,
        materialId: items[0].materialId,
        value: totalQuantity,
        date: items[0].date,
        orderId: monthKey,
        clientName: monthKey,
        actualPrice: totalQuantity > 0 ? totalActualPrice / totalQuantity : 0,
        isGroup: true,
        groupKey: monthKey,
        children: items,
        totalQuantity,
        totalActualPrice,
      };

      return [groupHeader, ...items];
    });
};

export const groupSalesCommitsByCustomer = (salesCommits: SalesCommItem[]): GroupedSalesCommItem[] => {
  const grouped = salesCommits.reduce((acc, item) => {
    const customerKey = item.clientName || 'Unknown';
    if (!acc[customerKey]) {
      acc[customerKey] = [];
    }
    acc[customerKey].push(item);
    return acc;
  }, {} as Record<string, SalesCommItem[]>);

  return Object.entries(grouped)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .flatMap(([customerKey, items]) => {
      const totalQuantity = items.filter((item) => !item.dontSum).reduce((sum, item) => sum + item.value, 0);
      const totalActualPrice = items
        .filter((item) => !item.dontSum)
        .reduce((sum, item) => sum + (item.actualPrice || 0) * item.value, 0);
      const earliestDate = items.reduce(
        (earliest, item) => (item.date < earliest ? item.date : earliest),
        items[0].date,
      );

      const groupHeader: GroupedSalesCommItem = {
        id: `group-${customerKey}` as any,
        materialId: items[0].materialId,
        value: totalQuantity,
        date: earliestDate,
        orderId: customerKey,
        clientName: customerKey,
        actualPrice: totalQuantity > 0 ? totalActualPrice / totalQuantity : 0,
        isGroup: true,
        groupKey: customerKey,
        children: items,
        totalQuantity,
        totalActualPrice,
      };

      return [groupHeader, ...items];
    });
};
